import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Banknote, Landmark, Receipt, UsersRound, Wallet } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Card, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { StatCard } from '@/components/ui/StatCard'
import { familyFeesQuery } from '@/features/finance/api'
import { InvoiceStatusBadge } from '@/features/finance/components/InvoiceStatusBadge'
import { localizePeriodLabel, methodLabel } from '@/features/finance/status'
import { familyQuery } from '@/features/portal/api'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'
import { formatDate, formatMoney, formatNumber } from '@/utils/format'

function ChildFees({ studentId }: { studentId: number }) {
  const { t } = useTranslation('finance')
  const { data, isPending, error, refetch } = useQuery(familyFeesQuery(studentId))
  if (isPending) return <LoadingState />
  if (error) return <ErrorState onRetry={() => void refetch()} />
  const money = (v: string) => formatMoney(v, data.currency)
  const s = data.summary

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t('Balance due')}
          value={money(s.outstanding)}
          icon={<Landmark />}
          tone="brand"
          hint={t('{{count}} open invoices', {
            count: s.openInvoiceCount,
            formatted: formatNumber(s.openInvoiceCount),
          })}
        />
        <StatCard label={t('Overdue')} value={money(s.overdue)} icon={<AlertTriangle />} tone="danger" />
        <StatCard label={t('Paid')} value={money(s.totalPaid)} icon={<Banknote />} tone="success" />
        <StatCard
          label={t('Billed')}
          value={money(s.totalInvoiced)}
          icon={<Receipt />}
          tone="info"
          hint={t('{{count}} invoices', { count: s.invoiceCount, formatted: formatNumber(s.invoiceCount) })}
        />
      </div>

      <Card>
        <CardHeader
          title={t('Invoices')}
          icon={<Receipt />}
          description={t('Contact the school office to pay or query an invoice.')}
        />
        {data.invoices.length === 0 ? (
          <EmptyState title={t('No invoices yet')} compact />
        ) : (
          <ul className="divide-y divide-line">
            {data.invoices.map((i) => (
              <li key={i.id} className="px-5 py-4">
                <details className="group">
                  <summary className="flex cursor-pointer list-none flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-ink">{localizePeriodLabel(i.periodLabel)}</p>
                      <p className="text-xs text-muted">
                        <span className="font-mono">{i.number}</span> ·{' '}
                        {t('due {{date}}', { date: formatDate(i.dueDate) })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-ink tabular">{money(i.total)}</p>
                      {i.outstanding !== '0.00' && i.status !== 'cancelled' ? (
                        <p className="text-xs text-muted tabular">
                          {t('{{amount}} due', { amount: money(i.outstanding) })}
                        </p>
                      ) : null}
                    </div>
                    <InvoiceStatusBadge status={i.status} />
                  </summary>
                  <table className="mt-3 w-full text-sm">
                    <caption className="sr-only">{t('Lines of {{number}}', { number: i.number })}</caption>
                    <tbody>
                      {i.lines.map((l) => (
                        <tr key={l.id} className="border-t border-line">
                          <td className="py-2 text-ink-soft">
                            {l.name}
                            {l.discountAmount !== '0.00' ? (
                              <span className="ml-1 text-xs text-muted">
                                ({l.discountName} −{money(l.discountAmount)})
                              </span>
                            ) : null}
                          </td>
                          <td className="py-2 text-right tabular">{money(l.netAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader title={t('Payments')} icon={<Wallet />} />
        {data.payments.length === 0 ? (
          <EmptyState title={t('No payments recorded')} compact />
        ) : (
          <ul className="divide-y divide-line">
            {data.payments.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink">
                    <span className="font-mono text-xs">{p.receiptNumber}</span> · {t(methodLabel[p.method])}
                  </p>
                  <p className="text-xs text-muted">
                    {formatDate(p.paidOn)} · {localizePeriodLabel(p.invoice.periodLabel)}
                  </p>
                </div>
                {p.isReversed ? (
                  <Badge tone="danger" size="sm">
                    {t('Reversed')}
                  </Badge>
                ) : null}
                <span
                  className={cn(
                    'text-sm font-semibold tabular',
                    p.isReversed ? 'text-muted line-through' : 'text-ink',
                  )}
                >
                  {money(p.amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}

export function FamilyFeesPage() {
  const { t } = useTranslation('finance')
  useDocumentTitle('Fees')
  const { data, isPending, error, refetch } = useQuery(familyQuery())
  const [selected, setSelected] = useState(0)

  if (isPending) return <LoadingState />
  if (error) return <ErrorState onRetry={() => void refetch()} />
  const child = data[selected] ?? data[0]

  return (
    <>
      <PageHeader
        title={t('Fees')}
        description={t('Invoices, payments and the balance due. This view is read-only.')}
      />
      {data.length > 1 ? (
        <div role="tablist" aria-label={t('Choose child')} className="mb-6 flex gap-2 overflow-x-auto">
          {data.map((c, i) => (
            <button
              key={c.student.id}
              role="tab"
              type="button"
              aria-selected={i === selected}
              onClick={() => setSelected(i)}
              className={cn(
                'flex shrink-0 items-center gap-2 rounded-full border py-1 pr-4 pl-1 text-sm font-medium transition-colors',
                i === selected
                  ? 'border-brand-300 bg-brand-50 text-brand-700'
                  : 'border-line bg-surface text-ink-soft hover:border-line-strong',
              )}
            >
              <Avatar name={c.student.fullName} src={c.student.photoUrl} size="sm" />
              {c.student.firstName}
            </button>
          ))}
        </div>
      ) : null}
      {child ? (
        <ChildFees key={child.student.id} studentId={child.student.id} />
      ) : (
        <Card>
          <EmptyState
            icon={<UsersRound />}
            title={t('No student linked to your account')}
            description={t("Ask the school office to link your login to your child's record.")}
          />
        </Card>
      )}
    </>
  )
}
